
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import CurrencyConverter from "../common/currency-converter.vue";
import ResponsiveDialog from "./responsive-dialog.vue";

export default {
  components: { ResponsiveDialog, CurrencyConverter },
  props: {
    dialog: Boolean,
    isMenuItemUpdate: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: () => {}
    },
    addonInfo: { type: Object, default: () => {} },
    selectedIndex: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      title: "",
      selectedAddons: [],
      isMinSelectionMandatory: false,
      minSelection: null,
      maxSelection: null,
      showMinSelectionWarning: false,
      valid: false,
      defaultTitleCounter: 1,
      showNoAddonsWarning: false
    };
  },
  computed: {
    ...mapState("menu", ["addons", "menuUploadList"])
  },
  beforeMount() {
    // Initialize defaultTitleCounter based on existing groups
    const existingTitles = this.isMenuItemUpdate
      ? this.item?.item_addons?.items?.map((addon) => addon.name)
      : this.item?.addons?.map((addon) => addon.groupName);
    const maxExistingNumber = existingTitles?.reduce((max, title) => {
      if (title === "Untitled Group") {
        // Treat "Untitled Group" as "Untitled Group 1"
        return Math.max(max, 1);
      }
      const match = title?.match(/Untitled Group (\d+)/);
      return match ? Math.max(max, parseInt(match[1], 10)) : max;
    }, 0);
    this.defaultTitleCounter =
      (isNaN(maxExistingNumber) ? 0 : maxExistingNumber) + 1;
    //
    if (this.addonInfo) {
      if (this.isMenuItemUpdate) {
        this.title = this.addonInfo.name;
        if (this.addonInfo?.addons) {
          const selectedAddons = this.addonInfo?.addons.map((item) => item.id);
          this.selectedAddons = JSON.parse(JSON.stringify(selectedAddons));
        } else {
          this.selectedAddons = JSON.parse(
            JSON.stringify(this.addonInfo?.selectedItems)
          );
        }
        // this.maxSelection = this.selectedAddons.length;
        this.minSelection = this.addonInfo.min_selection;
        this.isMinSelectionMandatory = this.minSelection ? true : false;
        this.maxSelection = this.addonInfo.max_selection;
      } else {
        this.title = this.addonInfo.groupName || this.getDefaultTitle();

        this.selectedAddons = JSON.parse(
          JSON.stringify(this.addonInfo?.selectedItems)
        );
        this.maxSelection = this.addonInfo?.selectedItems?.length;
        this.minSelection = this.addonInfo.minSelection;
        this.isMinSelectionMandatory = this.minSelection ? true : false;
        this.maxSelection = this.addonInfo.maxSelection;
      }
    }
  },
  methods: {
    ...mapMutations("menu", ["UPDATE_ADDON_GROUP", "MAP_ADDON_TO_ITEM"]),
    addAddon() {
      const data = {
        groupInfo: {
          groupName: this.title,
          selectedItems: this.selectedAddons,
          minSelection: this.isMinSelectionMandatory ? this.minSelection : 0,
          maxSelection: this.maxSelection
        },
        index: this.selectedIndex
      };
      this.$emit("add-new-addon", data);
    },
    isDefaultGroupName(groupName) {
      return /^Untitled Group(\s\d+)?$/.test(groupName);
    },
    decreaseMin() {
      if (this.minSelection > 1) {
        this.minSelection--;
      }
      if (this.minSelection < this.maxSelection) {
        this.showMinSelectionWarning = false;
      }
    },
    increaseMin() {
      if (this.minSelection < this.maxSelection || !this.maxSelection) {
        this.showMinSelectionWarning = false;
        this.minSelection++;
      } else {
        this.showMinSelectionWarning = true;
      }
    },

    handleIsMinSelectionMandatory() {
      this.minSelection = this.isMinSelectionMandatory ? 1 : null;
    },
    handleMaxSelectionChange() {
      if (this.minSelection === this.maxSelection) {
        this.showMinSelectionWarning = false;
      }
    },
    handleAddonSelectionChange(addon) {
      if (this.selectedAddons?.length) {
        this.maxSelection = this.selectedAddons.length;
        if (
          this.minSelection > this.maxSelection &&
          this.isMinSelectionMandatory
        ) {
          this.minSelection = this.selectedAddons.length;
        }
      }
    },
    isSelectionDisabled(addonId) {
      return (
        !this.selectedAddons.includes(addonId) &&
        this.selectedAddons.length >= this.maxSelection
      );
    },
    handleSaveAddonGroup() {
      if (!this.selectedAddons.length) {
        this.showNoAddonsWarning = true;
        return;
      } else {
        this.showNoAddonsWarning = false;
      }
      if (this.isMenuItemUpdate) {
        this.saveAddonGroup();
      } else {
        this.assignAddonsToItem();
      }
    },
    saveAddonGroup() {
      const completeAddonObjects = this.addons.filter((addon) =>
        this.selectedAddons.includes(addon.id)
      );
      const itemAddonInfo = {
        name: "",
        addons: completeAddonObjects,
        desc: "",
        min_selection: this.isMinSelectionMandatory ? this.minSelection : 0,
        max_selection: this.maxSelection
      };
      itemAddonInfo.name =
        this.addonInfo &&
        !this.title &&
        this.isDefaultGroupName(this.addonInfo.name)
          ? this.addonInfo.name
          : this.title || this.getDefaultTitle();
      this.$emit("add-addon-group", itemAddonInfo);
      this.$emit("close-dialog");
    },
    getDefaultTitle() {
      if (this.defaultTitleCounter === 1) {
        return "Untitled Group";
      } else {
        return `Untitled Group ${this.defaultTitleCounter++}`;
      }
    },
    assignAddonsToItem() {
      const itemAddonInfo = {
        groupName: "",
        selectedItems: this.selectedAddons,
        minSelection: this.isMinSelectionMandatory ? this.minSelection : 0,
        maxSelection: this.maxSelection
      };
      itemAddonInfo.groupName =
        this.addonInfo &&
        !this.title &&
        this.isDefaultGroupName(this.addonInfo.groupName)
          ? this.addonInfo.groupName
          : this.title || this.getDefaultTitle();

      if (typeof this.selectedIndex === "number") {
        this.UPDATE_ADDON_GROUP({
          itemId: this.item.id,
          addonIndex: this.selectedIndex,
          addonInfos: itemAddonInfo
        });
      } else {
        this.MAP_ADDON_TO_ITEM({
          itemId: this.item.id,
          addonInfo: itemAddonInfo
        });
      }
      this.selectedAddons = [];
      this.$nuxt.$emit("reapply-filter");
      this.$emit("close-dialog");
    }
  }
};
